/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useMemo, ChangeEvent } from 'react';
import { Button, Box, MenuItem, Select, SelectChangeEvent, Typography, TextField } from '@mui/material';
import { createNewClientSecret } from '../../services/client';
import Loader from '../../shared/Loader';
import useStyle from './styles';
import { convertTimeStamp, addDaysToDate } from '../../utility/dateUtility';
import MachineSecretCopyDialog from '../MachineSecretCopyDialog/MachineSecretCopyDialog';

import {
  ADD_CLIENT_SECRET_TITLE,
  CLIENT_SECRET_TITLE,
  BUTTON_ADD_CLIENT_SECRET,
  BUTTON_CANCEL,
  BUTTON_CLOSE,
  PERIOD_OPTIONS_LIST,
  CLIENT_SECRET_CREATE_SUCCESS_MESSAGE,
  CLIENT_SECRET_CREATE_FAILURE_MESSAGE,
  CREATE_CLIENT_SECRET_BTN,
  VALIDATION_MESSAGES
} from './constant';
import { EXPIRES_IN_DAYS_MESSAGE } from '../../utility/constants';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
import { hideSnackbar } from '../../store/snackBar/snackBarActionCreator';
import { LuiModal } from '@jda/lui-common-component-library-mui5';


const numberRegExp = /^\d*$/;

interface IAddClientSecretDialogProps {
  openModal: boolean;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
  refreshOnSave: Function;
  displaySnackbar: Function;
  hideSnackbar: Function;
  machineId: string;
  maxExpiryDays?: number;
  isModern?: boolean;
  machineType?: string;
}
const AddSecretDialog = ({
  machineId,
  maxExpiryDays,
  refreshOnSave,
  openModal,
  openCloseModal,
  displaySnackbar,
  isModern,
  machineType,
}: IAddClientSecretDialogProps) => {
  const [numberInputValue, setNumberInputValue] = useState(() => (maxExpiryDays ? `${maxExpiryDays}` : '90'));
  const [period, setPeriod] = useState(1);
  const [today] = useState(() => new Date());
  const [showLoader, showHideLoader] = useState(false);
  const [secretText, updateSecretText] = useState('');
  const [copyClicked, setCopyClicked] = useState(false);
  const [displayCopySecretOption, enableDisplayCopySecretOption] = useState(false);

  const expiryDays = useMemo(() => Number(numberInputValue) * period, [numberInputValue, period]);

  const validationError = useMemo(() => {
    if (!numberInputValue) {
      return { message: VALIDATION_MESSAGES.VALUE_REQUIRED };
    }

    if (expiryDays === 0) {
      return { message: VALIDATION_MESSAGES.VALUE_BIGGER_THAN_ZERO };
    }

    if (maxExpiryDays && expiryDays > maxExpiryDays) {
      return {
        message: interpolateString([`${maxExpiryDays}`], VALIDATION_MESSAGES.MAXIMUM_DAYS_ALLOWED),
        showPeriodError: true
      };
    }

    return undefined;
  }, [numberInputValue, expiryDays, maxExpiryDays]);

  const { classes } = useStyle();
  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const closeCopySecretDialog = () => {
    refreshOnSave(machineId);
    displaySnackbar(CLIENT_SECRET_CREATE_SUCCESS_MESSAGE, 'success');
    handleClose();
  };

  const handleNumberInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (numberRegExp.test(value)) {
      setNumberInputValue(value);
    }
  };

  const handlePeriodChange = (event: SelectChangeEvent<any>) => {
    setPeriod(event.target.value);
  };

  const handleSubmitClick = () => {
    hideSnackbar();
    showHideLoader(true);
    enableDisplayCopySecretOption(false);
    const payload = { expiryDays };
    if (isModern || machineType?.toLowerCase() === 'efs') {
      Object.assign(payload, { isModern: true });
    }
    createNewClientSecret(machineId, payload).then(response => {
      if (response) {
        showHideLoader(false);
        enableDisplayCopySecretOption(true);
        if (response.secretText) {
          updateSecretText(response.secretText);
        }
      }
    }).catch((error: any) => {
      showHideLoader(false);
      displaySnackbar(interpolateString([error.message], CLIENT_SECRET_CREATE_FAILURE_MESSAGE), 'error');
    });
  };

  const displayExpiryMessage = () => {
    const expiryDate = addDaysToDate(today, expiryDays);
    return `${interpolateString([`${expiryDays}`], EXPIRES_IN_DAYS_MESSAGE)} ${convertTimeStamp(expiryDate)}`;
  };

  return (
    <LuiModal
      modalSize="sm"
      isOpen={openModal}
      modalTitle={displayCopySecretOption ? CLIENT_SECRET_TITLE : ADD_CLIENT_SECRET_TITLE}
      handleClose={handleClose}
      primaryAction={
        <Button
          variant={displayCopySecretOption ? 'contained' : 'outlined'}
          name={displayCopySecretOption ? 'closeBtn' : 'cancelBtn'}
          color="primary"
          disableElevation
          disabled={showLoader || (displayCopySecretOption && !copyClicked)}
          onClick={displayCopySecretOption ? closeCopySecretDialog : handleClose}
        >
          {displayCopySecretOption ? BUTTON_CLOSE : BUTTON_CANCEL}
        </Button>
      }
      secondaryAction={
        !displayCopySecretOption && (
          <Button
            variant="contained"
            name={CREATE_CLIENT_SECRET_BTN}
            color="primary"
            disableElevation
            disabled={Boolean(validationError || showLoader)}
            onClick={handleSubmitClick}
          >
            {BUTTON_ADD_CLIENT_SECRET}
          </Button>
        )
      }
    >
      <Box className={classes.clientSecretContainer}>
        {!displayCopySecretOption && !showLoader && (
          <>
            <Box className={classes.expiryDurationControls}>
              <TextField
                name="numberInput"
                inputProps={{ inputMode: 'numeric', pattern: '\\d*', 'data-testid': 'numberInput' }}
                value={numberInputValue}
                error={Boolean(validationError)}
                onChange={handleNumberInputChange}
                className={classes.numberInput}
              />
              <Select
                name="period"
                value={period}
                variant='standard'
                error={validationError?.showPeriodError}
                onChange={handlePeriodChange}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                }}
                className={classes.periodSelect}
              >
                {PERIOD_OPTIONS_LIST.map(option => (
                  <MenuItem key={option.value} value={option.value} className={classes.periodOption}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box className={classes.expiryDurationMessage}>
              {validationError ? (
                <ErrorMessage message={validationError.message} />
              ) : (
                <Typography color="textSecondary" variant="body2">
                  {displayExpiryMessage()}
                </Typography>
              )}
            </Box>
          </>
        )}

        {displayCopySecretOption && !showLoader && (
          <MachineSecretCopyDialog
            secretText={secretText}
            displaySnackbar={displaySnackbar}
            setCopyClicked={setCopyClicked}
          ></MachineSecretCopyDialog>
        )}

        <Loader showLoader={showLoader} isCenter={true} />
      </Box>
    </LuiModal>
  );
};

export default AddSecretDialog;
